import { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowLeft, ArrowRight, Loader2, RefreshCcw, Save, Upload } from 'lucide-react';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { api, BASE, getToken } from '@/lib/api';
import { usePageParams } from '@/hooks/usePageParams';

type QcCheckResult = 'PASS' | 'FAIL' | 'N/A' | '';

type QcRecordLine = {
  id: string;
  line_role: string | null;
  qty: number | null;
  reason_text: string | null;
  notes: string | null;
};

type QcRecordDetail = {
  id: string;
  qc_number: string | null;
  reference_number: string | null;
  reference_type: string | null;
  sku_code: string | null;
  sku_name: string | null;
  project_code: string | null;
  inspector_name: string | null;
  qty_inspected: number | null;
  qty_passed: number | null;
  qty_failed: number | null;
  qty_na?: number | null;
  status: string | null;
  sla_due_at: string | null;
  failure_reason: string | null;
  notes: string | null;
  srfNo?: string | null;
  irtNo?: string | null;
  materialIdentification?: QcCheckResult | null;
  heatNoVerification?: QcCheckResult | null;
  millCertVerification?: QcCheckResult | null;
  dimensionVerification?: QcCheckResult | null;
  visualCondition?: QcCheckResult | null;
  lines: QcRecordLine[];
};

type AttachmentRow = {
  id: string;
  filename: string | null;
  mime_type: string | null;
  size_bytes: number | null;
  created_at: string | null;
};

type QcDraft = {
  inspectorName: string;
  qtyInspected: string;
  qtyPassed: string;
  qtyFailed: string;
  qtyNa: string;
  materialIdentification: QcCheckResult;
  heatNoVerification: QcCheckResult;
  millCertVerification: QcCheckResult;
  dimensionVerification: QcCheckResult;
  visualCondition: QcCheckResult;
  failureReason: string;
  notes: string;
};

type WmsQcRecordDetailPanelProps = {
  id?: string;
  embedded?: boolean;
  onClose?: () => void;
};

const QC_RESULT_OPTIONS = [
  { label: 'Accept', value: 'PASS' },
  { label: 'Reject', value: 'FAIL' },
  { label: 'N/A', value: 'N/A' },
] as const;

function formatStatusLabel(status: string | null | undefined) {
  if (!status) return 'Unknown';
  return status
    .toLowerCase()
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function statusTone(status: string | null | undefined) {
  switch (String(status ?? '').toUpperCase()) {
    case 'PASSED':
      return 'bg-green-100 text-green-700 border-green-200';
    case 'PARTIAL':
    case 'PENDING':
      return 'bg-amber-100 text-amber-700 border-amber-200';
    case 'FAILED':
      return 'bg-red-100 text-red-700 border-red-200';
    default:
      return 'bg-slate-100 text-slate-700 border-slate-200';
  }
}

function formatDateTime(value: string | null | undefined) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

function createDraft(detail: QcRecordDetail): QcDraft {
  return {
    inspectorName: detail.inspector_name ?? '',
    qtyInspected: String(detail.qty_inspected ?? 0),
    qtyPassed: String(detail.qty_passed ?? 0),
    qtyFailed: String(detail.qty_failed ?? 0),
    qtyNa: String(detail.qty_na ?? 0),
    materialIdentification: detail.materialIdentification ?? '',
    heatNoVerification: detail.heatNoVerification ?? '',
    millCertVerification: detail.millCertVerification ?? '',
    dimensionVerification: detail.dimensionVerification ?? '',
    visualCondition: detail.visualCondition ?? '',
    failureReason: detail.failure_reason ?? '',
    notes: detail.notes ?? '',
  };
}

function HeaderField({
  label,
  value,
  className = '',
}: {
  label: string;
  value: string | null | undefined;
  className?: string;
}) {
  return (
    <div className={className}>
      <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-1 min-h-8 text-sm font-medium text-foreground">{value && value.trim() ? value : '-'}</div>
    </div>
  );
}

export function WmsQcRecordDetailPanel({
  id: idProp,
  embedded = false,
  onClose,
}: WmsQcRecordDetailPanelProps) {
  const params = usePageParams<{ id: string }>();
  const id = idProp ?? params.id;

  const [detail, setDetail] = useState<QcRecordDetail | null>(null);
  const [draft, setDraft] = useState<QcDraft | null>(null);
  const [attachments, setAttachments] = useState<AttachmentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const fetchAttachments = useCallback(async () => {
    if (!id) return;
    const rows = await api.get<{ items?: AttachmentRow[] }>(
      `/wms/attachments?referenceType=${encodeURIComponent('QC')}&referenceId=${encodeURIComponent(id)}`,
    );
    setAttachments(rows.items ?? []);
  }, [id]);

  const fetchDetail = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError('');
    try {
      const response = await api.get<{ data?: QcRecordDetail } | QcRecordDetail>(`/wms/qc/${id}`);
      const payload = (response as { data?: QcRecordDetail })?.data ?? response as QcRecordDetail;
      setDetail(payload);
      setDraft(createDraft(payload));
      await fetchAttachments();
    } catch (err: any) {
      setError(err?.message ?? 'Failed to load QC record');
    } finally {
      setLoading(false);
    }
  }, [fetchAttachments, id]);

  useEffect(() => {
    void fetchDetail();
  }, [fetchDetail]);

  const dirty = useMemo(() => {
    if (!detail || !draft) return false;
    const initial = createDraft(detail);
    return JSON.stringify(initial) !== JSON.stringify(draft);
  }, [detail, draft]);

  const updateDraft = (patch: Partial<QcDraft>) => {
    setDraft((current) => current ? { ...current, ...patch } : current);
  };

  const resetDraft = () => {
    if (!detail) return;
    setDraft(createDraft(detail));
  };

  const handleUpload = async (files: FileList | null) => {
    if (!files || files.length === 0 || !id) return;
    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        const form = new FormData();
        form.append('file', file);
        form.append('referenceType', 'QC');
        form.append('referenceId', id);
        const response = await fetch(`${BASE}/wms/attachments`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${getToken()}` },
          body: form,
        });
        if (!response.ok) {
          const payload = await response.json().catch(() => null);
          throw new Error(payload?.error?.message ?? 'Failed to upload attachment');
        }
      }
      toast.success('QC attachment uploaded');
      await fetchAttachments();
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to upload attachment');
    } finally {
      setUploading(false);
    }
  };

  const openAttachment = async (attachmentId: string) => {
    const response = await fetch(`${BASE}/wms/attachments/${encodeURIComponent(attachmentId)}/content`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    if (!response.ok) {
      throw new Error('Failed to open attachment');
    }
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank', 'noopener,noreferrer');
    window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
  };

  const saveDraft = async () => {
    if (!id || !draft) return;
    const qtyInspected = Number(draft.qtyInspected || 0);
    const qtyPassed = Number(draft.qtyPassed || 0);
    const qtyFailed = Number(draft.qtyFailed || 0);
    const qtyNa = 0;
    if (!Number.isFinite(qtyInspected) || !Number.isFinite(qtyPassed) || !Number.isFinite(qtyFailed)) {
      toast.error('QC quantities must be valid numbers');
      return;
    }
    if (Number((qtyPassed + qtyFailed).toFixed(4)) !== Number(qtyInspected.toFixed(4))) {
      toast.error('Passed Qty + Rejected Qty must equal Inspected Qty');
      return;
    }
    const missingChecks = [
      !draft.dimensionVerification ? 'Dimension' : '',
      !draft.materialIdentification ? 'Spec' : '',
      !draft.visualCondition ? 'Visual Condition' : '',
      !draft.heatNoVerification ? 'Heat/Cert No Check' : '',
    ].filter(Boolean);
    if (missingChecks.length > 0) {
      toast.error(`Please complete QC checks: ${missingChecks.join(', ')}`);
      return;
    }

    setSaving(true);
    try {
      await api.patch(`/wms/qc/${id}`, {
        inspectorName: draft.inspectorName.trim() || undefined,
        qtyInspected,
        qtyPassed,
        qtyFailed,
        qtyNa,
        materialIdentification: draft.materialIdentification || undefined,
        heatNoVerification: draft.heatNoVerification || undefined,
        millCertVerification: draft.millCertVerification || undefined,
        dimensionVerification: draft.dimensionVerification || undefined,
        visualCondition: draft.visualCondition || undefined,
        failureReason: draft.failureReason.trim() || undefined,
        notes: draft.notes.trim() || undefined,
      });
      toast.success('QC record updated');
      await fetchDetail();
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to update QC record');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-full min-h-[320px] items-center justify-center">
        <Loader2 className="mr-2 h-5 w-5 animate-spin text-muted-foreground" />
        <span className="text-sm text-muted-foreground">Loading QC record...</span>
      </div>
    );
  }

  if (error || !detail || !draft) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <div className="rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error || 'QC record not found'}
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col bg-background">
      <div className="shrink-0 border-b bg-background px-6 py-5">
        <div>
          <div className="pr-10">
            <div className="text-lg font-semibold tracking-tight">QC : {detail.qc_number ?? '-'}</div>
            <div className="mt-2">
              <Badge className={`border ${statusTone(detail.status)}`}>{formatStatusLabel(detail.status)}</Badge>
            </div>
          </div>
          <div className="mt-4 flex flex-wrap items-center justify-start gap-2">
            {dirty ? (
              <>
                <Button variant="outline" size="sm" onClick={resetDraft} disabled={saving}>
                  <RefreshCcw className="mr-1.5 h-3.5 w-3.5" />
                  Reset
                </Button>
                <Button size="sm" onClick={() => void saveDraft()} disabled={saving}>
                  {saving ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Save className="mr-1.5 h-3.5 w-3.5" />}
                  Save
                </Button>
              </>
            ) : null}
            {embedded && onClose ? <Button variant="outline" size="sm" onClick={onClose}>Close</Button> : null}
            {!embedded ? (
              <Button variant="outline" size="sm" onClick={() => window.history.back()}>
                <ArrowLeft className="mr-1.5 h-3.5 w-3.5" />
                Back
              </Button>
            ) : null}
          </div>
        </div>

        <div className="mt-5 grid grid-cols-1 gap-x-4 gap-y-3 lg:grid-cols-6">
          <HeaderField label="QC No" value={detail.qc_number} />
          <HeaderField label="Reference" value={detail.reference_number} />
          <HeaderField label="SKU" value={detail.sku_code} />
          <HeaderField label="Project" value={detail.project_code} />
          <HeaderField label="SRF No" value={detail.srfNo ?? null} />
          <HeaderField label="IRT No" value={detail.irtNo ?? null} />
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-auto px-6 py-4">
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-6">
          <div className="space-y-1 xl:col-span-2">
            <Label className="text-xs text-muted-foreground">Item Description</Label>
            <Input className="h-9" value={detail.sku_name ?? ''} readOnly />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Inspector</Label>
            <Input className="h-9" value={draft.inspectorName} onChange={(e) => updateDraft({ inspectorName: e.target.value })} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Inspected Qty</Label>
            <Input className="h-9" type="number" min="0" step="0.01" value={draft.qtyInspected} onChange={(e) => updateDraft({ qtyInspected: e.target.value })} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Passed Qty</Label>
            <Input className="h-9" type="number" min="0" step="0.01" value={draft.qtyPassed} onChange={(e) => updateDraft({ qtyPassed: e.target.value })} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Rejected Qty</Label>
            <Input className="h-9" type="number" min="0" step="0.01" value={draft.qtyFailed} onChange={(e) => updateDraft({ qtyFailed: e.target.value })} />
          </div>
        </div>

        <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {([
            ['dimensionVerification', 'Dimension'],
            ['materialIdentification', 'Spec'],
            ['visualCondition', 'Visual Condition'],
            ['heatNoVerification', 'Heat/Cert No Check'],
          ] as const).map(([field, label]) => (
            <div key={field} className="space-y-1">
              <Label className="text-xs text-muted-foreground">{label}</Label>
              <Select
                value={draft[field] || '__unset__'}
                onValueChange={(value) => updateDraft({ [field]: value === '__unset__' ? '' : value } as Partial<QcDraft>)}
              >
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__unset__">-</SelectItem>
                  {QC_RESULT_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ))}
        </div>

        <div className="mt-4 grid gap-3 xl:grid-cols-6">
          <div className="space-y-1 xl:col-span-2">
            <Label className="text-xs text-muted-foreground">Reject Reason</Label>
            <Input className="h-9" value={draft.failureReason} onChange={(e) => updateDraft({ failureReason: e.target.value })} />
          </div>
          <div className="space-y-1 xl:col-span-4">
            <Label className="text-xs text-muted-foreground">Notes</Label>
            <Textarea rows={3} value={draft.notes} onChange={(e) => updateDraft({ notes: e.target.value })} />
          </div>
        </div>

        <div className="mt-5 rounded-md border">
          <div className="flex items-center justify-between border-b px-4 py-3">
            <div>
              <div className="text-sm font-semibold">Attachments</div>
              <div className="text-xs text-muted-foreground">Document upload is optional.</div>
            </div>
            <Label className="inline-flex cursor-pointer items-center gap-2 rounded-md border px-3 py-1.5 text-xs font-medium hover:bg-muted">
              <Upload className="h-3.5 w-3.5" />
              {uploading ? 'Uploading...' : 'Add Attachment'}
              <Input
                type="file"
                multiple
                className="hidden"
                disabled={uploading}
                onChange={(event) => {
                  void handleUpload(event.target.files);
                  event.currentTarget.value = '';
                }}
              />
            </Label>
          </div>
          <div className="p-4">
            {attachments.length === 0 ? (
              <div className="rounded-md border border-dashed px-3 py-6 text-center text-sm text-muted-foreground">
                No attachments yet
              </div>
            ) : (
              <div className="overflow-auto rounded border">
                <table className="min-w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="px-3 py-2 text-left">File</th>
                      <th className="px-3 py-2 text-left">Type</th>
                      <th className="px-3 py-2 text-left">Created</th>
                      <th className="px-3 py-2 text-right">Open</th>
                    </tr>
                  </thead>
                  <tbody>
                    {attachments.map((attachment) => (
                      <tr key={attachment.id} className="border-t">
                        <td className="px-3 py-2 font-medium">{attachment.filename ?? '-'}</td>
                        <td className="px-3 py-2">{attachment.mime_type ?? '-'}</td>
                        <td className="px-3 py-2">{formatDateTime(attachment.created_at)}</td>
                        <td className="px-3 py-2 text-right">
                          <Button size="sm" variant="outline" onClick={() => void openAttachment(attachment.id)}>
                            <ArrowRight className="mr-1.5 h-3.5 w-3.5" />
                            Open
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        <div className="mt-5 rounded-md border">
          <div className="border-b px-4 py-3">
            <div className="text-sm font-semibold">Lines</div>
          </div>
          <div className="overflow-auto p-4">
            <table className="min-w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-3 py-2 text-left">Role</th>
                  <th className="px-3 py-2 text-left">Qty</th>
                  <th className="px-3 py-2 text-left">Reason</th>
                  <th className="px-3 py-2 text-left">Notes</th>
                </tr>
              </thead>
              <tbody>
                {(detail.lines ?? []).map((line) => (
                  <tr key={line.id} className="border-t">
                    <td className="px-3 py-2">{line.line_role ?? '-'}</td>
                    <td className="px-3 py-2">{line.qty ?? 0}</td>
                    <td className="px-3 py-2">{line.reason_text ?? '-'}</td>
                    <td className="px-3 py-2">{line.notes ?? '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
